// =========================================================================================@@
// Last Updated Date: May 18, 2025
// Last Updated By: Steven
// Status Level: 3
// ===========================================================================================

import React from 'react'
import { useApolloClient } from '@apollo/client'
import PropTypes from 'prop-types'
import { validateDiscount as validateDiscountGql } from 'src/core/graphql/queries/validateDiscount.gql'
import { Button } from 'src/sites/kits/UI2'

const OrderSummary = ({
   discounts,
   initiativeId,
   loading,
   orderTotals,
   setDiscounts
}) => {
   const client = useApolloClient()

   const clearDiscounts = () => {
      setDiscounts([])
   }

   const handleApplyDiscountCode = async () => {
      const discountCode = window.prompt('Please enter your discount code')
      if (!discountCode) {
         return
      }

      try {
         const result = await client.query({
            query: validateDiscountGql,
            variables: {
               code: discountCode,
               initiativeId
            }
         })

         if (result.data.validateDiscount) {
            setDiscounts([result.data.validateDiscount])
         }
      } catch (err) {
         window.alert(err.message)
      }
   }

   return (
      <div className="bg-slate-100 text-slate-900 rounded-xl px-5">
         <div className="flex items-center h-14 text-sm tracking-tight font-semibold">Order Summary</div>
         {orderTotals.discounted > 0 && (
            <div className="text-sm text-slate-600 py-2 flex items-center justify-between border-t border-solid border-slate-300/50">
               <div>
                  {`Discount (${discounts.map(d => d.code).join(', ')})`}
                  &nbsp;
                  <button type="button" className="inline ml-2 text-slate-700" onClick={clearDiscounts}>
                     <u>Remove</u>
                  </button>
               </div>
               <div>
                  {orderTotals.discounted.toLocaleString('en-US', {
                     style: 'currency',
                     currency: 'USD'
                  })}
               </div>
            </div>
         )}
         <div className="text-sm text-slate-600 py-2 flex items-center justify-between border-t border-solid border-slate-300/50">
            <div>
               Subtotal
            </div>
            <div>
               {orderTotals.subtotal.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD'
               })}
            </div>
         </div>
         {orderTotals.taxes.map(tax => (
            <div className="text-sm py-2 flex items-center justify-between border-t border-solid border-slate-300/50">
               <div className="font-semibold">
                  {tax.name}
               </div>
               <div>
                  {tax.amount.toLocaleString('en-US', {
                     style: 'currency',
                     currency: 'USD'
                  })}
               </div>
            </div>
         ))}
         <div className="py-2 flex items-center justify-between border-t border-solid border-slate-300/50">
            <div className="text-sm font-semibold">
               Total
            </div>
            <div className="text-lg tracking-tight font-semibold">
               {orderTotals.total.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD'
               })}
            </div>
         </div>
         {discounts.length > 0 && <div className="w-full h-3" />}
         {discounts.length === 0 && (
            <Button
               onClick={handleApplyDiscountCode}
               className="bg-slate-400 text-white mb-5"
               size="xs">
               Apply Discount Code
            </Button>
         )}
      </div>
   )
}

OrderSummary.propTypes = {
   discounts: PropTypes.array.isRequired,
   initiativeId: PropTypes.string.isRequired,
   loading: PropTypes.bool,
   orderTotals: PropTypes.object.isRequired,
   setDiscounts: PropTypes.func.isRequired
}

OrderSummary.defaultProps = {
   loading: false
}

export default OrderSummary
